/**
 * Shared sanitization utilities for the Baakas application
 * Consolidates duplicate sanitization functions across components
 * 
 * Note: For HTML content that will be rendered, use sanitizeHtml() which uses DOMPurify.
 * For plain text inputs, use sanitizeString().
 */
import DOMPurify from 'dompurify';

// Security: Enhanced input sanitization
export function sanitizeString(input: any, maxLength: number = 100): string {
  if (!input || typeof input !== 'string') return '';
  
  // Don't modify Firebase URLs to preserve token parameters
  if (input.includes('firebasestorage.googleapis.com') || input.includes('firebasestorage.app')) {
    return input.trim().slice(0, maxLength);
  }
  
  // Remove potentially dangerous characters (but preserve & for URLs)
  const sanitized = input
    .replace(/[<>"'&]/g, '') // Remove dangerous characters
    .trim()
    .slice(0, maxLength);
    
  return sanitized.length > 0 ? sanitized : '';
}

// Security: Enhanced URL sanitization
export function sanitizeUrl(input: string, maxLength: number = 500): string {
  if (!input || typeof input !== 'string') return '';
  
  let sanitized = input.trim();
  
  // Remove dangerous characters (but preserve & for query strings)
  sanitized = sanitized.replace(/[<>"']/g, '');
  
  // Remove javascript: and data: URLs (XSS vectors)
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/data:text\/html/gi, '');
  sanitized = sanitized.replace(/data:image\/svg\+xml/gi, '');
  sanitized = sanitized.replace(/vbscript:/gi, '');
  
  // Don't modify Firebase URLs to preserve token parameters (but still validate protocol)
  // Firebase Storage URLs can be long (up to 2000+ chars with tokens), so use larger limit
  if (sanitized.includes('firebasestorage.googleapis.com') || sanitized.includes('firebasestorage.app')) {
    try {
      const url = new URL(sanitized);
      if (!['http:', 'https:'].includes(url.protocol)) {
        return '';
      }
      // Firebase Storage URLs can be up to 2000+ characters, allow full length
      const firebaseMaxLength = 2500;
      return sanitized.slice(0, firebaseMaxLength);
    } catch {
      // If URL parsing fails, return empty for Firebase URLs
      return '';
    }
  }
  
  // Validate URL format and protocol for other URLs
  try {
    const url = new URL(sanitized);
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(url.protocol)) {
      return '';
    }
  } catch {
    // If URL parsing fails, check if it's a relative path (starts with /)
    if (!sanitized.startsWith('/')) {
      return '';
    }
  }
  
  // Limit length for other URLs
  return sanitized.slice(0, maxLength);
}

// Security: Enhanced email sanitization
export function sanitizeEmail(email: string): string | null {
  if (!email || typeof email !== 'string') return null;
  const e = email.trim().toLowerCase();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(e) && e.length <= 254 ? e : null;
}

// Security: Enhanced error sanitization
export function sanitizeError(error: any): string {
  if (error instanceof Error) {
    // Check for Firestore permission errors first - these are often non-critical for profile creation
    // Don't show "Access denied" for Firestore errors during user profile creation
    if ((error.message.includes('permission-denied') || error.message.includes('permission denied')) &&
        (error.message.includes('Firestore') || error.message.includes('Users') || error.message.includes('profile'))) {
      // For Firestore permission errors during profile operations, return a more helpful message
      // or let it be handled silently by the calling code
      return 'Profile creation failed. Please try again or contact support if the issue persists.';
    }
    
    // Only return generic error messages, not detailed stack traces or sensitive info
    // Check for general permission/unauthorized errors (but not Firestore profile errors)
    if (error.message.includes('permission') || error.message.includes('unauthorized')) {
      return 'Access denied';
    }
    if (error.message.includes('not-found') || error.message.includes('missing')) {
      return 'Resource not found';
    }
    if (error.message.includes('network') || error.message.includes('timeout')) {
      return 'Network error occurred';
    }
    if (error.message.includes('quota') || error.message.includes('limit')) {
      return 'Service temporarily unavailable';
    }
    // Preserve email verification messages - they're already user-friendly
    if (error.message.includes('verify your email') || 
        error.message.includes('email verification') ||
        error.message.includes('email address before signing')) {
      return error.message; // Preserve the original helpful message
    }
    // Preserve Google sign-in specific error messages
    if (error.message.includes('Google sign-in') || 
        error.message.includes('Google authentication') ||
        error.message.includes('popup') ||
        error.message.includes('redirect') ||
        error.message.includes('localhost') ||
        error.message.includes('authorized') ||
        error.message.includes('domain') ||
        error.message.includes('not supported') ||
        error.message.includes('contact support') ||
        error.message.includes('not enabled')) {
      // Check if it's a user-friendly message (longer than 30 chars usually means it's descriptive)
      if (error.message.length > 30) {
        return error.message;
      }
    }
    // Auth-specific error messages
    if (error.message.includes('auth') || error.message.includes('login')) {
      return 'Authentication failed';
    }
    if (error.message.includes('password') || error.message.includes('wrong-password')) {
      return 'Invalid credentials';
    }
    // Only generic email errors should be converted to "Invalid credentials"
    if (error.message.includes('user-not-found')) {
      return 'Invalid credentials';
    }
    return 'Operation failed';
  }
  return 'Unknown error occurred';
}

// ============================================
// HTML Sanitization (using DOMPurify)
// Use these functions for HTML content that will be rendered with dangerouslySetInnerHTML
// ============================================

/**
 * Sanitize HTML content for safe rendering
 * @param html - HTML string to sanitize
 * @param options - Optional DOMPurify configuration
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(html: string, options?: DOMPurify.Config): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Default configuration - safe for most use cases
  const defaultOptions: DOMPurify.Config = {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
  };

  const config = { ...defaultOptions, ...options };
  return DOMPurify.sanitize(html, config);
}

/**
 * Sanitize plain text (removes all HTML)
 * @param text - Text to sanitize
 * @returns Plain text with HTML removed
 */
export function sanitizeText(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

/**
 * Sanitize HTML for product descriptions
 * Allows more formatting options
 * SECURITY: XSS Protection - Uses DOMPurify with strict configuration
 */
export function sanitizeProductDescription(html: string): string {
  // SECURITY: DoS Protection - Limit input size to prevent memory exhaustion
  const MAX_DESCRIPTION_LENGTH = 50000; // 50KB max
  if (!html || typeof html !== 'string') {
    return '';
  }
  
  // SECURITY: Truncate if too long to prevent DoS
  const truncatedHtml = html.length > MAX_DESCRIPTION_LENGTH 
    ? html.substring(0, MAX_DESCRIPTION_LENGTH) 
    : html;
  
  // SECURITY: XSS Protection - Use DOMPurify with strict config (no attributes allowed)
  return sanitizeHtml(truncatedHtml, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    ALLOWED_ATTR: [], // SECURITY: No attributes allowed to prevent XSS
    ALLOW_DATA_ATTR: false, // SECURITY: Disable data attributes
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'style', 'link', 'meta'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'style', 'href', 'src'], // SECURITY: Block event handlers and dangerous attributes
  });
}

/**
 * Sanitize HTML for user messages
 * Very restrictive for security
 */
export function sanitizeUserMessage(html: string): string {
  return sanitizeHtml(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em'],
    ALLOWED_ATTR: [],
  });
}

/**
 * Sanitize phone number
 * Removes non-digit characters except + at the start
 */
export function sanitizePhoneNumber(phone: string | null | undefined): string {
  if (!phone || typeof phone !== 'string') return '';
  // Keep + at start if present, then keep only digits
  const cleaned = phone.trim();
  if (cleaned.startsWith('+')) {
    return '+' + cleaned.slice(1).replace(/\D/g, '');
  }
  return cleaned.replace(/\D/g, '');
}
